// Install-to-device plumbing, kept out of React on purpose.
//
// Chrome fires `beforeinstallprompt` as soon as the app meets its
// installability bar, which here means "once the service worker is running" —
// and PwaUpdater only registers that worker after a successful sign-in
// (Cars.jsx renders <Home> only when logInStatus === true). The event can
// therefore land before, during or after the mount of whatever wants to show
// the invitation, and it fires exactly once per page load: miss it and there
// is no way to ask for it again.
//
// So the listener is attached at module scope, imported first thing in
// main.jsx, and the event is parked here for whoever turns up later.

const SNOOZE_KEY = 'pwaInstallSnoozedUntil';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000; // "not now" means a month, not forever

let deferredPrompt = null;
let justInstalled = false;
const listeners = new Set();

const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', (e) => {
        // Without preventDefault Chrome shows its own mini-infobar, which we
        // would then be duplicating.
        e.preventDefault();
        deferredPrompt = e;
        notify();
    });

    // Fires for installs we prompted for AND ones the user did through the
    // browser's own menu, so this is the only reliable "stop asking" signal.
    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        justInstalled = true;
        try { localStorage.removeItem(SNOOZE_KEY); } catch { /* private mode */ }
        notify();
    });
}

/** Subscribe to changes in install availability. Returns an unsubscribe fn. */
export function subscribeToInstallState(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

export function canInstall() {
    return deferredPrompt !== null;
}

export function wasJustInstalled() {
    return justInstalled;
}

/** Already running as an installed app? Then there is nothing to offer. */
export function isStandalone() {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(display-mode: standalone)').matches
        || window.navigator.standalone === true; // iOS's own flag
}

/**
 * iOS has no beforeinstallprompt and no programmatic install at all — Safari
 * only installs through Share ▸ Add to Home Screen. Detected so we can show
 * those steps instead of a button that cannot work.
 *
 * iPadOS reports itself as "MacIntel", so the touch-point check is what
 * separates an iPad from a desktop Mac.
 */
export function isIos() {
    if (typeof window === 'undefined') return false;
    const ua = window.navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua)
        || (ua.includes('Macintosh') && window.navigator.maxTouchPoints > 1);
}

/** On iOS, only Safari can add to the home screen. */
export function isIosSafari() {
    if (!isIos()) return false;
    const ua = window.navigator.userAgent;
    // Chrome/Firefox/Edge on iOS are Safari-engined but cannot install.
    return !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

export function isSnoozed() {
    try {
        const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
        return Number.isFinite(until) && Date.now() < until;
    } catch {
        return false; // private mode: better to ask than to stay silent forever
    }
}

export function snooze() {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)); }
    catch { /* private mode — the invite just reappears next session */ }
}

/**
 * Show the browser's install dialog.
 * Resolves 'accepted' | 'dismissed' | 'unavailable'.
 */
export async function promptInstall() {
    const e = deferredPrompt;
    if (!e) return 'unavailable';
    // A given beforeinstallprompt event can only be prompt()ed once, so drop
    // it now — including when the user says no. Chrome re-fires the event on a
    // later load if the app is still installable.
    deferredPrompt = null;
    notify();
    try {
        e.prompt();
        const { outcome } = await e.userChoice;
        return outcome;
    } catch {
        return 'unavailable';
    }
}
